import React, { useCallback, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { LinkExternal } from "@/components/LinkExternal";
import { useProgramId, useProgramTheme, useVerificationResponse } from "@/lib/state/contextHooks";
import { getFaqLink, getPrivacyPolicyUrl } from "@/lib/ProgramTheme/programThemeGetters";
import { standardizeLocale } from "@/lib/intl/intl";
import { useIntlValues } from "@/lib/intl/useIntlValues";
import { CLOUDFLARE_PRIVACY_POLICY_URL, FEEDBACK_FORM_URL, SUPPORT_URL } from "@/constants";
import { type Locale, type Segment } from "@/lib/types/types";
import { type MessageId } from "@/lib/intl/types";

export type MsgFunction = (args: { id: MessageId; context?: string }) => string;

/**
 * A custom hook to perform plain text internationalization
 * @returns a function that takes an id and context and returns an internationalized string message in the current language
 */
export function useMessage() {
  const { i18n, t } = useTranslation();
  const values = useIntlValues();

  const msg: MsgFunction = useCallback(
    ({ id, context }) => {
      return t(id, {
        context,
        defaultValue: `{${id}}`,
        replace: values,
      });
    },
    [t, values],
  );

  return { i18n, msg };
}

function MessageLink(props: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { href, children } = props;
  if (!URL.canParse(href)) return <>{children}</>;

  const url = new URL(href);
  // Limited set of supported protocols
  if (!["http:", "https:", "tel:", "mailto:"].includes(url.protocol)) return <>{children}</>;

  // We will only support a target "_top"; in all other cases use "_blank"
  const target = props.target === "_top" ? "_top" : "_blank";

  return (
    <a
      href={url.toString()}
      target={target}
      rel={target === "_top" ? "noopener noreferrer" : undefined}
      className="sid-link"
    >
      {React.Children.count(children) > 0 ? children : url.toString()}
    </a>
  );
}

function useFaqHref(): string {
  const { i18n } = useTranslation();
  const programId = useProgramId();
  const programTheme = useProgramTheme();
  const verificationResponse = useVerificationResponse();
  const segment = (verificationResponse as { segment?: Segment })?.segment;

  return getFaqLink(
    programTheme,
    segment,
    programId,
    i18n.language as Locale,
    verificationResponse,
  );
}

function AboutSheerID({ children }: { children?: React.ReactElement[] }) {
  const faqHref = useFaqHref();

  return (
    <LinkExternal href={faqHref} className="sid-faq sid-link">
      {children}
    </LinkExternal>
  );
}

function FaqLink({ children }: { children?: React.ReactElement[] }) {
  const faqHref = useFaqHref();
  if (!faqHref) return children;

  return (
    <LinkExternal href={faqHref} className="sid-link">
      {children}
    </LinkExternal>
  );
}

function PrivacyPolicyLink({ children }: { children?: React.ReactElement[] }) {
  const programTheme = useProgramTheme();
  const privacyPolicyHref = getPrivacyPolicyUrl(programTheme);

  if (!privacyPolicyHref) return children;
  return (
    <LinkExternal href={privacyPolicyHref} className="sid-privacy-policy sid-link">
      {children}
    </LinkExternal>
  );
}

function CloudflarePrivacyPolicyLink({ children }: { children?: React.ReactElement[] }) {
  return (
    <LinkExternal href={CLOUDFLARE_PRIVACY_POLICY_URL} className="sid-privacy-policy sid-link">
      {children}
    </LinkExternal>
  );
}

function FeedbackFormLink({
  programId,
  locale,
  children,
}: {
  programId: string;
  locale: Locale;
  children?: React.ReactElement[];
}) {
  return (
    <LinkExternal
      href={`${FEEDBACK_FORM_URL}&pid=${programId}&locale=${standardizeLocale(locale)}`}
      className="sid-link"
    >
      {children}
    </LinkExternal>
  );
}

function TipsLink({
  type,
  locale,
  children,
}: {
  type: "selfie" | "image";
  locale: Locale;
  children?: React.ReactElement[];
}) {
  return (
    <MessageLink
      href={`${SUPPORT_URL}/${standardizeLocale(locale)}/help-center/${type}-tips`}
      className="sid-link"
    >
      {children}
    </MessageLink>
  );
}

function InlineChildrenOnly({ children }: { children?: React.ReactElement[] }) {
  // fragment required as <Trans> components must be typed as ReactElement and not string.
  return <>{children}</>;
}

function UnsupportedBlockElement() {
  // TODO: Update this with a translation for "element not supported"
  // e.g. return <b>[Tables are not supported.]</b>;
  return null;
}

function UnsupportedInlineElement() {
  // TODO: Update this with a translation for "element not supported"
  // return <b>[Tables are not supported.]</b>;
  return null;
}

export function Message({ id, context }: { id: MessageId; context?: string }) {
  const { t } = useTranslation();

  const values = useIntlValues();
  const { programId, locale } = values;

  /**
   * ⚠️ component functions utilized here must only implement explicit properties
   * and never rely on "spread" properties. This will ensure that only supported
   * attributes added to tags in messages are rendered to HTML.
   */
  const components = useMemo(
    () => ({
      // DSL tags
      aboutSheerID: <AboutSheerID />,
      cloudflarePrivacyPolicy: <CloudflarePrivacyPolicyLink />,
      feedbackFormLink: <FeedbackFormLink programId={programId || ""} locale={locale} />,
      privacyPolicy: <PrivacyPolicyLink />,
      selfieTipsLink: <TipsLink type="selfie" locale={locale} />,
      imageTipsLink: <TipsLink type="image" locale={locale} />,
      faqLink: <FaqLink />,
      // basic HTML tags
      a: <MessageLink />,
      br: <br />,
      b: <strong />,
      strong: <strong />,
      i: <em />,
      em: <em />,
      code: <InlineChildrenOnly />,
      span: <span />,
      p: <p />,
      ul: <ul />,
      ol: <ol />,
      li: <li />,
      u: <InlineChildrenOnly />,
      // unsupport CommonMark tags - Inline elements
      img: <UnsupportedInlineElement />,
      // unsupport CommonMark tags - Block elements
      h1: <div />,
      h2: <div />,
      h3: <div />,
      h4: <div />,
      h5: <div />,
      h6: <div />,
      hr: <UnsupportedBlockElement />,
      pre: <div />,
      blockquote: <div />,
      table: <UnsupportedBlockElement />,
    }),
    [programId, locale],
  );

  return (
    <Trans i18nKey={id as string} context={context} t={t} components={components} values={values} />
  );
}
